import { BriefcaseBusiness, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import Image from 'next/image';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { faqs } from '@/lib/aboutPage';
import Link from 'next/link';

const FAQSection = () => {
  // 1. Generate JSON-LD for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.faq.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <section 
      id="faq"
      className='w-full py-12 md:py-24 px-6 lg:px-12 bg-white'
      aria-labelledby="faq-heading"
    >
      {/* 2. SEO: Inject Schema.org data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className='max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start'>

        {/* Left Column: Content */}
        <div className='flex flex-col gap-10 lg:sticky lg:top-24'>
          <header className="flex flex-col gap-3">
            <div className="flex items-center gap-2 font-bold text-[#4F7AC2] tracking-widest uppercase text-xs sm:text-sm">
              <BriefcaseBusiness className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
              <span>Digital marketing FAQ</span>
            </div>
            <h2 
              id="faq-heading"
              className='mb-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#001D4A] leading-[1.1] tracking-tight'
            >
              {faqs.title}
            </h2>
            <p className="max-w-xl text-base sm:text-lg text-gray-600 leading-relaxed font-normal">
              {faqs.description}
            </p>
          </header>

          <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-4'>
            <Button 
              asChild
              className='text-sm shadow-md px-8 lg:px-12 py-6 bg-[#4F7AC2] text-white rounded-md font-semibold hover:bg-[#001D4A] transition-all'
            >
              <Link href={faqs.buttonLink} aria-label="Get Started — Contact us today">
                {faqs.button}
                <ArrowRight className='ml-2 w-4 h-4' aria-hidden="true" />
              </Link>
            </Button>
            
         
          </div>

          {/* Decorative Image with specific Aspect Ratio to prevent Layout Shift */}
          <div className='relative aspect-[4/3] w-full rounded-2xl overflow-hidden shadow-2xl hidden lg:block'>
            <Image
              src='/images/faq.jpg'
              alt='Professional customer support representative helping a client'
              fill
              className='object-cover'
              sizes="(max-width: 1024px) 100vw, 40vw"
              loading="lazy"
            />
          </div>
        </div>

        {/* Right Column: Accordion */}
        <div className='w-full'>
          <Accordion
            type="single"
            collapsible
            className="w-full space-y-4"
          >
            {faqs.faq.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="group bg-slate-50 data-[state=open]:bg-white border border-slate-100 rounded-xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <AccordionTrigger
                  className="px-6 sm:px-8 text-[#001D4A] data-[state=open]:bg-[#4F7AC2] data-[state=open]:text-white text-base sm:text-lg hover:no-underline text-left font-bold py-5 transition-all duration-300"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="px-6 py-6 sm:px-8 text-gray-600 text-sm sm:text-base leading-relaxed">
                    {faq.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

      </div>
    </section>
  );
};

export default FAQSection;